import { useEffect, useRef } from 'react';
import { GridApi, GridOptions } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { GroupingColumnId, Row, UseControlledAGGridProps } from '../types';
import { defaultComparator } from '../utils';
import { getMenuItems } from '../menuItems';

export const useColumnGrouping = <
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
>(
  props: UseControlledAGGridProps<RowData, FullWidthRowData, RowId>,
  api: GridApi | null
) => {
  const columnGrouping = props.columnGrouping;
  const columnGroupingEnabled = columnGrouping !== undefined;
  const columnGroupingRef = useRef(columnGrouping);
  useEffect(() => {
    columnGroupingRef.current = columnGrouping;
  }, [columnGrouping]);
  const columnGroupingGroups = columnGrouping?.groups?.map(String);
  const pinningEnabled = props.pinnable !== undefined;
  const columnChooserEnabled = props.enableColumnChooser === true;

  useEffect(() => {
    if (!api || !columnGroupingEnabled || !columnGroupingGroups) {
      return;
    }

    /*
     * We need to see if the current grouping is different from the external grouping.
     * If it is, we need to update the internal state.
     * This is to prevent AG Grid from calling `onColumnRowGroupChanged` when we
     * pass in the same grouping.
     */
    const currentGrouping = api.getRowGroupColumns();
    const currentGroupIds = currentGrouping.map(col => col.getColId());
    const hasChanged =
      currentGroupIds.length !== columnGroupingGroups.length ||
      currentGroupIds.some((id, index) => id !== columnGroupingGroups[index]);

    if (hasChanged) {
      api.setRowGroupColumns(columnGroupingGroups);
    }
  }, [api, columnGroupingEnabled, columnGroupingGroups]);

  const columnGroupingProps: GridOptions<Row<RowData, RowId>> | undefined =
    columnGroupingEnabled
      ? {
          onColumnRowGroupChanged: event => {
            const currentColumnGrouping = columnGroupingRef.current;
            if (!currentColumnGrouping) {
              return;
            }

            const newGrouping = event.api.getRowGroupColumns();
            const newGroupIds = newGrouping.map(col =>
              col.getColId()
            ) as GroupingColumnId<RowData>[];

            currentColumnGrouping.onGroupingChange(newGroupIds);
          },
          rowGroupPanelShow: 'always',
          autoGroupColumnDef: {
            comparator: (a, b) =>
              columnGrouping.comparator?.(a ?? undefined, b ?? undefined) ??
              defaultComparator(a, b),
            mainMenuItems: params =>
              getMenuItems(
                params,
                pinningEnabled,
                columnChooserEnabled,
                columnGroupingEnabled
              ),
            cellRendererParams: {
              innerRenderer: (
                params: CustomCellRendererProps<Row<RowData, RowId>>
              ) => params.node.key,
              suppressDoubleClickExpand: true,
            },
          },
        }
      : undefined;

  return { columnGroupingProps, columnGroups: columnGrouping?.groups ?? [] };
};
